import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'

/**
 * On-demand loader for the two Stylitics vendor scripts.
 *
 * Behind the `ssr_stylitics_on_demand_load` Statsig gate (read in `_document`): gate off keeps the
 * legacy `<Script strategy="beforeInteractive">` tags in `_document` — ~520 KB of classic deferred
 * JS that executes on every US/CA page *before* React hydrates, even though only the PDP "Shop the
 * Look" widget (composer) and the Stylitics Insights telemetry integration read the globals. In
 * that mode the globals already exist when a consumer runs and this loader resolves immediately.
 *
 * Gate on: `_document` renders no tags and the consumer awaits `loadStyliticsScript(kind)`, which
 * injects the script the first time something actually needs it, after hydration by construction.
 * The returned promise resolves `true` once the global is available and `false` when the script
 * must not / could not load (SSR, UK countryCompliant locale, network error), so callers keep their
 * existing "no composer → render fallback" paths and there is no ordering race with hydration.
 */
export const STYLITICS_SCRIPT_SRC = {
  composer: 'https://web-assets.stylitics.com/loader/latest/composer.js',
  insights: 'https://web-assets.stylitics.com/insights-script/latest/stylitics-insights-script_iife.js',
} as const

export type StyliticsScriptKind = keyof typeof STYLITICS_SCRIPT_SRC

const STYLITICS_GLOBAL: Record<StyliticsScriptKind, 'StyliticsComposer' | 'StyliticsInsights'> = {
  composer: 'StyliticsComposer',
  insights: 'StyliticsInsights',
}

const pendingLoads = new Map<StyliticsScriptKind, Promise<boolean>>()

function hasGlobal(kind: StyliticsScriptKind): boolean {
  return (
    typeof window !== 'undefined' && Boolean((window as unknown as Record<string, unknown>)[STYLITICS_GLOBAL[kind]])
  )
}

/**
 * Loads (once) the requested Stylitics script and resolves when its global is usable.
 * Never loads for countryCompliant locales (UK): the Composer drops a `styliticsWidgetSession`
 * cookie and Insights is a 3P analytics tracker — same exclusion `_document` applied before.
 */
export function loadStyliticsScript(kind: StyliticsScriptKind): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false)
  if (hasGlobal(kind)) return Promise.resolve(true)
  if (getIsCountryCompliant()) return Promise.resolve(false)

  const pending = pendingLoads.get(kind)
  if (pending) return pending

  const src = STYLITICS_SCRIPT_SRC[kind]
  const load = new Promise<boolean>(resolve => {
    const settle = (ok: boolean) => {
      if (!ok) pendingLoads.delete(kind) // allow a later retry after a network failure
      resolve(ok && hasGlobal(kind))
    }
    // Always create a fresh tag. Never adopt a `<script>` that is already in the DOM: an errored tag
    // fires no further events, so awaiting it would hang forever (and `pendingLoads` already
    // dedupes in-flight loads from this module).
    const el = document.createElement('script')
    el.addEventListener(
      'load',
      () => {
        settle(true)
      },
      { once: true },
    )
    el.addEventListener(
      'error',
      () => {
        el.remove() // a failed <script> never fires again; keep the DOM clean for the retry
        settle(false)
      },
      { once: true },
    )
    el.src = src
    el.async = true
    // `_document` exposes the CSP nonce for scripts added at runtime (`window.__CSP_NONCE__`);
    // 'strict-dynamic' already trusts appendChild-inserted scripts, the nonce keeps the
    // no-strict-dynamic fallback policy happy too.
    const nonce = (window as unknown as { __CSP_NONCE__?: string }).__CSP_NONCE__
    if (nonce) el.setAttribute('nonce', nonce)
    // eslint-disable-next-line i18next/no-literal-string
    el.setAttribute('data-nscript', 'stylitics-on-demand')
    document.body.appendChild(el)
  })
  pendingLoads.set(kind, load)
  return load
}

/** Test-only: forget in-flight loads between cases. */
export function resetStyliticsScriptLoaderForTests(): void {
  pendingLoads.clear()
}
